<template>
  <FreeBottomSheet :name="name" @onOpen="onSheetOpen" @onClose="onSheetClose">
    <template #header="{ hide }">
      <FreeHeader :is-centered="flowStep !== 'error'" class="p-4">
        <template #leftAction>
          <div
            v-if="!headerShowBack || flowStep === 'error'"
            class="h-6 w-6 shrink-0"
            aria-hidden="true"
          />
          <FreeBackButton
            v-else
            data-qa-id="passengersData.documentPhoto.header.back"
            @click="onHeaderBack"
          />
        </template>
        <template #default>
          <h2
            v-if="sheetTitle"
            class="text-text-neutral-primary line-clamp-2 text-center text-lg leading-6 font-bold"
            :data-qa-id="sheetTitleQaId"
          >
            {{ sheetTitle }}
          </h2>
        </template>
        <template #rightAction>
          <FreeCloseButton
            v-if="showCloseInHeader"
            data-qa-id="passengersData.documentPhoto.close"
            @click="hide"
          />
          <div v-else class="w-7 shrink-0" aria-hidden="true" />
        </template>
      </FreeHeader>
    </template>
    <template #default="{ hide }">
      <input
        ref="cameraInputRef"
        type="file"
        class="sr-only"
        :accept="DOCUMENT_MEDIA_ACCEPT.image"
        capture="environment"
        data-qa-id="passengersData.documentPhoto.input.camera"
        @change="onFileInput"
      />
      <input
        ref="galleryInputRef"
        type="file"
        class="sr-only"
        :accept="DOCUMENT_MEDIA_ACCEPT.image"
        :multiple="galleryMultiple"
        data-qa-id="passengersData.documentPhoto.input.gallery"
        @change="onFileInput"
      />
      <input
        ref="docsInputRef"
        type="file"
        class="sr-only"
        :accept="DOCUMENT_MEDIA_ACCEPT.imageAndPdf"
        :multiple="docsMultiple"
        data-qa-id="passengersData.documentPhoto.input.documents"
        @change="onFileInput"
      />

      <!-- source (V3 Source) -->
      <div v-if="flowStep === 'source'" class="flex flex-col px-4 pb-8">
        <p
          class="text-text-neutral-secondary my-2 pt-1 pb-2 text-xs"
          data-qa-id="passengersData.documentPhoto.source.subtitle"
        >
          {{ t('passengersData.documentPhoto.source.subtitle') }}
        </p>
        <div class="flex flex-col gap-2">
          <button
            v-for="row in sourceRows"
            :key="row.key"
            type="button"
            class="v3-source-tile flex items-center gap-3.5 rounded-2xl border-0 p-3.5 text-left shadow-[0_1px_2px_rgba(0,0,0,0.04)]"
            :data-qa-id="`passengersData.documentPhoto.source.${row.key}`"
            @click="row.onClick"
          >
            <div
              class="v3-icon-halo flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-xl"
            >
              <V3DocumentIcons :name="row.icon" color="#4ebc73" />
            </div>
            <div class="min-w-0 flex-1">
              <div
                class="text-text-neutral-primary text-[15px] leading-tight font-semibold"
              >
                {{
                  t(`passengersData.documentPhoto.source.rows.${row.key}.title`)
                }}
              </div>
              <div class="text-text-neutral-tertiary text-xs">
                {{
                  t(`passengersData.documentPhoto.source.rows.${row.key}.desc`)
                }}
              </div>
            </div>
            <V3DocumentIcons name="chev" color="var(--v3-chevron, #8e8e93)" />
          </button>
        </div>
        <div
          class="v3-hint-card mt-4 mb-3 flex items-center gap-2.5 rounded-[14px] p-3 pl-3.5"
        >
          <div class="shrink-0">
            <V3DocumentIcons name="file" color="#4ebc73" />
          </div>
          <p
            class="text-text-neutral-primary text-xs"
            data-qa-id="passengersData.documentPhoto.source.fileHint"
          >
            {{ t('passengersData.documentPhoto.source.fileHint') }}
          </p>
        </div>
        <button
          type="button"
          class="text-text-neutral-secondary mt-2 h-12 w-full cursor-pointer border-0 bg-transparent text-base font-bold"
          data-qa-id="passengersData.documentPhoto.source.cancel"
          @click="hide"
        >
          {{ t('passengersData.documentPhoto.source.cancel') }}
        </button>
      </div>

      <!-- photos (PhotosUploadSheet) -->
      <div
        v-else-if="flowStep === 'photos'"
        class="flex min-h-0 flex-1 flex-col px-4 pb-4"
      >
        <div class="v3-hint-card mb-3.5 flex gap-2.5 rounded-[14px] p-3 pl-3.5">
          <div class="mt-0.5 shrink-0">
            <V3DocumentIcons name="sparkle" color="#4ebc73" />
          </div>
          <p
            class="text-text-neutral-primary text-xs"
            data-qa-id="passengersData.documentPhoto.photos.hintCard"
          >
            {{ t('passengersData.documentPhoto.photos.hintCard') }}
          </p>
        </div>
        <div class="mb-2 flex items-center justify-between">
          <span
            class="text-text-neutral-secondary text-xs font-semibold"
            data-qa-id="passengersData.documentPhoto.photos.counter"
            >{{
              t('passengersData.documentPhoto.photos.counter', {
                n: pickedItems.length,
                max: maxFiles,
              })
            }}</span
          >
          <button
            v-if="pickedItems.length > 0"
            type="button"
            class="text-xs font-bold"
            :class="v3LinkClass"
            data-qa-id="passengersData.documentPhoto.photos.clear"
            @click="clearPhotos"
          >
            {{ t('passengersData.documentPhoto.photos.clear') }}
          </button>
        </div>
        <div class="mt-2 mb-2 grid grid-cols-2 gap-2">
          <div
            v-for="(item, idx) in pickedItems"
            :key="item.id"
            class="relative aspect-3/4 overflow-hidden rounded-xl shadow-[0_1px_2px_rgba(0,0,0,0.04)]"
            :data-qa-id="`passengersData.documentPhoto.photos.preview.${idx}`"
          >
            <img
              v-if="item.previewUrl"
              :src="item.previewUrl"
              :alt="item.file.name"
              class="h-full w-full object-cover"
              loading="lazy"
              decoding="async"
            />
            <div
              v-else
              class="bg-background-neutral-secondary text-text-neutral-secondary flex h-full w-full flex-col items-center justify-center p-1 text-center text-xs"
            >
              PDF
              <span class="line-clamp-2 break-all opacity-80">{{
                item.file.name
              }}</span>
            </div>
            <button
              type="button"
              class="absolute top-1.5 right-1.5 flex h-[22px] w-[22px] items-center justify-center rounded-full border-0"
              :class="v3RemoveClass"
              :data-qa-id="`passengersData.documentPhoto.photos.remove.${idx}`"
              :aria-label="t('passengersData.documentPhoto.photos.remove')"
              @click="removeItem(item.id)"
            >
              <svg
                width="10"
                height="10"
                viewBox="0 0 12 12"
                stroke="white"
                stroke-width="2"
                stroke-linecap="round"
                aria-hidden="true"
              >
                <line x1="2" y1="2" x2="10" y2="10" />
                <line x1="10" y1="2" x2="2" y2="10" />
              </svg>
            </button>
            <div
              class="text-2xs absolute bottom-1.5 left-1.5 rounded-md bg-black/55 px-1.5 py-0.5 font-semibold text-white"
            >
              {{ idx + 1 }}
            </div>
          </div>
          <button
            v-if="canAddFiles"
            type="button"
            class="border-text-neutral-tertiary/30 text-text-neutral-secondary bg-background-neutral-onblock flex aspect-3/4 flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed"
            data-qa-id="passengersData.documentPhoto.photos.add"
            @click="flowStep = 'source'"
          >
            <div
              class="flex h-7 w-7 items-center justify-center rounded-full"
              :class="v3AddCircleClass"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                stroke="#4ebc73"
                stroke-width="2"
                stroke-linecap="round"
                aria-hidden="true"
              >
                <line x1="7" y1="2" x2="7" y2="12" />
                <line x1="2" y1="7" x2="12" y2="7" />
              </svg>
            </div>
            <span class="text-2xs font-semibold">{{
              t('passengersData.documentPhoto.photos.addLabel')
            }}</span>
          </button>
        </div>
        <div class="mt-0.5 mt-2 mb-6 flex gap-2.5">
          <button
            v-for="act in photoActions"
            :key="act.key"
            type="button"
            class="text-text-neutral-primary flex h-[46px] flex-1 items-center justify-center gap-1.5 rounded-2xl border-0 text-sm font-semibold"
            :class="[
              v3ActionBtnClass,
              !canAddFiles ? 'cursor-not-allowed opacity-40' : '',
            ]"
            :disabled="!canAddFiles"
            :data-qa-id="`passengersData.documentPhoto.photos.action.${act.key}`"
            @click="act.onClick"
          >
            <V3DocumentIcons :name="act.icon" :color="v3ActionIconColor" />
            {{ t(`passengersData.documentPhoto.photos.actions.${act.key}`) }}
          </button>
        </div>
        <i18n-t
          keypath="passengersData.documentPhoto.photos.footnote"
          tag="p"
          class="text-text-neutral-tertiary px-1 py-2 text-xs leading-relaxed"
          data-qa-id="passengersData.documentPhoto.photos.footnote"
        >
          <template #publicOffer>
            <button
              type="button"
              class="text-text-positive-primary contents underline"
              @click="emit('onPublicOfferClick')"
            >
              {{ t('passengersData.documentPhoto.photos.publicOffer') }}
            </button>
          </template>
        </i18n-t>
        <div class="mt-auto pt-3">
          <button
            type="button"
            class="v3-btn-primary w-full border-0 py-3.5 text-base font-bold text-white"
            :disabled="pickedItems.length === 0"
            data-qa-id="passengersData.documentPhoto.photos.recognize"
            @click="onRecognize"
          >
            {{ t('passengersData.documentPhoto.photos.recognize') }}
          </button>
        </div>
      </div>

      <!-- scan -->
      <div
        v-else-if="flowStep === 'scanning'"
        class="flex flex-1 flex-col items-center gap-4 px-4 pt-1 pb-6"
        data-qa-id="passengersData.documentPhoto.scanning.block"
      >
        <div
          class="bg-background-neutral-onblock relative mt-1 h-[156px] w-60 max-w-full overflow-hidden rounded-[14px] shadow-[0_8px_24px_rgba(0,0,0,0.06)]"
        >
          <div class="absolute inset-3 flex gap-2.5" aria-hidden="true">
            <div
              class="bg-background-neutral-tertiary h-[62px] w-12 rounded-md"
            />
            <div class="flex min-w-0 flex-1 flex-col gap-1.5 pt-1">
              <div
                v-for="w in [72, 88, 52, 62]"
                :key="w"
                class="h-1.5 rounded"
                :class="skeletonClass"
                :style="{ width: `${w}%` }"
              />
            </div>
          </div>
          <div
            class="absolute top-[52%] right-0 left-0 h-0.5"
            :class="v3ScanlineClass"
          />
          <div
            v-for="(p, i) in cornerKeys"
            :key="i"
            class="absolute h-4 w-4 border-solid border-green-500"
            :class="p.cls"
            aria-hidden="true"
          />
        </div>
        <div class="text-center">
          <p
            class="text-text-neutral-primary mb-1 text-lg font-bold"
            data-qa-id="passengersData.documentPhoto.scanning.title"
          >
            {{ t('passengersData.documentPhoto.scanning.title') }}
          </p>
          <p
            class="text-text-neutral-secondary mx-auto max-w-[280px] text-xs"
            data-qa-id="passengersData.documentPhoto.scanning.subtitle"
          >
            {{ t('passengersData.documentPhoto.scanning.subtitle') }}
          </p>
        </div>
        <div class="mb-4 w-full max-w-[300px]">
          <div
            class="bg-background-neutral-onblock mt-4 mb-6 h-1.5 overflow-hidden rounded"
          >
            <div class="h-full w-[62%] bg-green-500 transition-all"></div>
          </div>
          <ul class="flex flex-col gap-2">
            <li
              v-for="st in progressSteps"
              :key="st.key"
              class="flex items-center gap-2"
            >
              <V3DocumentIcons
                v-if="st.state === 'done'"
                name="check"
                color="#4ebc73"
              />
              <div
                v-else-if="st.state === 'active'"
                class="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-green-500 border-t-transparent"
              />
              <div
                v-else
                class="border-background-neutral-tertiary bg-background-neutral-secondary h-4 w-4 shrink-0 rounded-full border"
              />
              <span
                class="text-text-neutral-primary text-sm"
                :class="st.state === 'active' ? 'font-semibold' : 'font-normal'"
                >{{
                  t(`passengersData.documentPhoto.scanning.progress.${st.key}`)
                }}</span
              >
            </li>
          </ul>
        </div>
      </div>

      <!-- review (V3_Review) -->
      <div
        v-else-if="flowStep === 'review'"
        class="flex min-h-0 flex-1 flex-col gap-3 px-4 pb-4"
      >
        <div
          class="mb-0.5 flex items-start gap-2.5 rounded-[14px] border p-3 pl-3.5 shadow-[0_1px_2px_rgba(0,0,0,0.04)]"
          :class="v3ReviewBannerClass"
        >
          <div class="w-5 shrink-0">
            <V3DocumentIcons name="sparkle" color="#d8a900" />
          </div>
          <p class="text-text-neutral-primary text-xs">
            {{ t('passengersData.documentPhoto.review.banner') }}
          </p>
        </div>
        <div class="min-h-0 flex-1 space-y-2 overflow-y-auto">
          <div
            v-for="field in reviewFields"
            :key="field.label"
            class="v3-field bg-background-neutral-secondary rounded-2xl border p-3.5"
            :class="
              field.isEmpty
                ? 'border-text-negative-primary'
                : v3FieldAccentClass
            "
          >
            <div
              class="text-text-neutral-secondary text-xs leading-4 font-medium"
            >
              {{ field.label }}
            </div>
            <div
              class="text-text-neutral-primary flex min-h-6 items-center justify-between gap-2 pt-0.5 text-base leading-6 font-medium"
            >
              <span class="min-w-0 wrap-break-word">{{ field.value }}</span>
            </div>
          </div>
        </div>
        <div class="flex flex-col gap-2 pt-1">
          <button
            type="button"
            class="w-full border-0 py-3.5 text-base font-bold"
            :class="
              hasEmptyFields ? 'v3-btn-disabled' : 'v3-btn-primary text-white'
            "
            :disabled="hasEmptyFields"
            data-qa-id="passengersData.documentPhoto.review.continue"
            @click="flowStep = 'confirm'"
          >
            {{ t('passengersData.documentPhoto.review.continue') }}
          </button>
          <button
            v-if="scanResult"
            type="button"
            class="w-full border-0 text-base font-semibold"
            :class="
              hasEmptyFields
                ? 'v3-btn-primary py-3.5 text-white'
                : 'text-text-neutral-secondary h-12 bg-transparent'
            "
            data-qa-id="passengersData.documentPhoto.review.edit"
            @click="emit('onEditRequest', scanResult)"
          >
            {{ t('passengersData.documentPhoto.review.editData') }}
          </button>
        </div>
      </div>

      <!-- confirm -->
      <div
        v-else-if="flowStep === 'confirm'"
        class="flex flex-1 flex-col px-4 pt-2 pb-4"
        data-qa-id="passengersData.documentPhoto.confirm"
      >
        <div
          class="mb-4 flex gap-3 rounded-2xl border border-[rgba(255,196,0,0.35)] bg-[rgba(255,196,0,0.12)] p-4"
        >
          <div class="mt-0.5 w-5 shrink-0">
            <V3DocumentIcons name="sparkle" color="#d8a900" />
          </div>
          <div>
            <p
              class="text-text-neutral-primary mb-1 text-base leading-snug font-semibold"
              data-qa-id="passengersData.documentPhoto.confirm.heading"
            >
              {{ t('passengersData.documentPhoto.confirm.heading') }}
            </p>
            <p
              class="text-text-neutral-secondary text-sm leading-relaxed"
              data-qa-id="passengersData.documentPhoto.confirm.body"
            >
              {{ t('passengersData.documentPhoto.confirm.body') }}
            </p>
          </div>
        </div>
        <div class="mt-auto flex flex-col gap-2">
          <button
            type="button"
            class="v3-btn-primary w-full border-0 py-3.5 text-base font-bold text-white"
            data-qa-id="passengersData.documentPhoto.confirm.confirm"
            @click="emit('onAutofillFlowComplete', scanResult ?? undefined)"
          >
            {{ t('passengersData.documentPhoto.confirm.confirm') }}
          </button>
          <button
            type="button"
            class="text-text-neutral-secondary h-12 w-full border-0 bg-transparent text-base font-semibold"
            data-qa-id="passengersData.documentPhoto.confirm.back"
            @click="flowStep = 'review'"
          >
            {{ t('passengersData.documentPhoto.confirm.back') }}
          </button>
        </div>
      </div>

      <!-- error (V3_Error) -->
      <div
        v-else-if="flowStep === 'error'"
        class="flex flex-1 flex-col items-center px-5 pt-5 pb-6"
        data-qa-id="passengersData.documentPhoto.error"
      >
        <div
          class="mb-5 flex flex-1 flex-col items-center justify-center gap-5"
        >
          <div
            class="flex h-14 w-14 items-center justify-center rounded-2xl"
            :class="v3ErrorIconWrap"
          >
            <svg
              class="h-7 w-7"
              viewBox="0 0 24 24"
              fill="none"
              :stroke="v3ErrorStroke"
              stroke-width="1.8"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path d="M12 2 1 21h22L12 2z" />
              <line x1="12" y1="9" x2="12" y2="14" />
              <circle
                cx="12"
                cy="17.5"
                r="0.8"
                fill="currentColor"
                stroke="none"
              />
            </svg>
          </div>
          <div class="text-center">
            <p
              class="text-text-neutral-primary mb-1.5 text-lg leading-6 font-bold"
            >
              {{
                errorKind === 'recognition'
                  ? t('passengersData.documentPhoto.error.title')
                  : t('passengersData.documentPhoto.error.technicalTitle')
              }}
            </p>
            <p
              class="text-text-neutral-secondary max-w-screen-xs mx-auto text-sm leading-relaxed"
            >
              {{
                errorKind === 'recognition'
                  ? t('passengersData.documentPhoto.error.subtitle')
                  : t('passengersData.documentPhoto.error.technicalSubtitle')
              }}
            </p>
          </div>
        </div>
        <div class="flex w-full flex-col gap-2 pt-10">
          <button
            type="button"
            class="v3-btn-primary w-full border-0 py-3.5 text-base font-bold text-white"
            data-qa-id="passengersData.documentPhoto.error.retry"
            @click="onErrorRetry"
          >
            {{ t('passengersData.documentPhoto.error.retry') }}
          </button>
          <button
            type="button"
            class="text-text-neutral-primary h-12 w-full border-0 bg-transparent text-base font-semibold"
            data-qa-id="passengersData.documentPhoto.error.manual"
            @click="emit('onAutofillFlowComplete')"
          >
            {{ t('passengersData.documentPhoto.error.manual') }}
          </button>
        </div>
      </div>
    </template>
  </FreeBottomSheet>
</template>

<script setup lang="ts">
  // Variation 3 (CTA-first): макет по React-референсу (V3_* / SheetPhone); в prod — FreeBottomSheet / FreeHeader / Free*.

  import { v4 as uuid } from 'uuid';
  import { computed, onBeforeUnmount, ref, type Ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import type { AxiosInstance } from 'axios';
  import {
    FreeBackButton,
    FreeBottomSheet,
    FreeCloseButton,
    FreeHeader,
  } from 'freedom-shared';
  import { citizenshipEntity } from 'shared';

  import {
    DOCUMENT_MEDIA_ACCEPT,
    MAX_DOCUMENT_AUTOFILL_FILES,
  } from '@/shared/lib/document-media-pick';

  import { docScanApi, type DocScanData } from '../api/doc-scan';
  import V3DocumentIcons from './document-flow/V3DocumentIcons.vue';

  const maxFiles = MAX_DOCUMENT_AUTOFILL_FILES;

  const props = defineProps<{
    name: string;
    apiClient?: AxiosInstance;
  }>();

  const emit = defineEmits<{
    onAutofillFlowComplete: [data?: DocScanData];
    onEditRequest: [data: DocScanData];
    onPublicOfferClick: [];
  }>();

  const { t, locale } = useI18n();

  type FlowStep =
    | 'source'
    | 'photos'
    | 'scanning'
    | 'review'
    | 'confirm'
    | 'error';
  type ProgressState = 'done' | 'active' | 'pending';
  type ProgressKey = 'uploaded' | 'fields' | 'form';
  type ErrorKind = 'recognition' | 'technical';

  const flowStep = ref<FlowStep>('source');
  const scanResult = ref<DocScanData | null>(null);
  const abortController = ref<AbortController | null>(null);
  const errorKind = ref<ErrorKind>('recognition');

  const v3LinkClass = 'text-[#4ebc73]';
  const v3RemoveClass = 'bg-black/55';
  const v3AddCircleClass = 'bg-[rgba(78,188,115,0.12)]';
  const v3ActionBtnClass = 'bg-button-secondary-default';
  const v3ActionIconColor = 'rgb(var(--text-neutral-primary))';
  const v3FieldAccentClass = 'border-[#4ebc73]';
  const v3ReviewBannerClass =
    'bg-[rgba(255,196,0,0.12)] border-[rgba(255,196,0,0.35)]';
  const v3ErrorIconWrap = 'bg-[rgba(255,196,0,0.12)]';
  const v3ErrorStroke = '#ffc400';
  const skeletonClass = 'bg-background-neutral-tertiary';
  const v3ScanlineClass =
    'bg-linear-to-r from-transparent via-[#4ebc73] to-transparent shadow-[0_0_18px_#4ebc73]';
  const cornerKeys = [
    { cls: 'left-1.5 top-1.5 border-b-0 border-l-2 border-r-0 border-t-2' },
    { cls: 'right-1.5 top-1.5 border-b-0 border-l-0 border-r-2 border-t-2' },
    { cls: 'bottom-1.5 left-1.5 border-2 border-r-0 border-t-0' },
    { cls: 'bottom-1.5 right-1.5 border-2 border-l-0 border-t-0' },
  ] as const;

  type PickedItem = { id: string; file: File; previewUrl: string | null };
  const pickedItems = ref<PickedItem[]>([]);
  const cameraInputRef: Ref<HTMLInputElement | null> = ref(null);
  const galleryInputRef: Ref<HTMLInputElement | null> = ref(null);
  const docsInputRef: Ref<HTMLInputElement | null> = ref(null);

  function getCitizenshipName(code: string): string {
    const country = citizenshipEntity.config.countryList.find(
      c => c.code === code,
    );
    if (!country) {
      return code;
    }
    const loc = locale.value;
    if (loc === 'kk') {
      return country.nameKK;
    }
    if (loc === 'en') {
      return country.nameEN;
    }
    return country.name;
  }

  function reformatApiDate(apiDate: string): string {
    const [year, month, day] = apiDate.split('-');
    return `${day}.${month}.${year}`;
  }

  const reviewFields = computed(() => {
    const p = 'passengersData.documentPhoto.review.fields';
    if (scanResult.value) {
      const d = scanResult.value;
      const genderLabels: Record<string, string> = {
        M: t('passengersData.documentPhoto.review.genderMale'),
        F: t('passengersData.documentPhoto.review.genderFemale'),
      };
      const genderValue = genderLabels[d.gender] ?? '';
      const fields = [
        {
          label: t(`${p}.surname.label`),
          value: d.last_name,
          isEmpty: !d.last_name,
        },
        {
          label: t(`${p}.name.label`),
          value: d.first_name,
          isEmpty: !d.first_name,
        },
        {
          label: t(`${p}.gender.label`),
          value: genderValue,
          isEmpty: !d.gender,
        },
        {
          label: t(`${p}.dob.label`),
          value: d.dob ? reformatApiDate(d.dob) : '',
          isEmpty: !d.dob,
        },
        {
          label: t(`${p}.citizenship.label`),
          value: d.citizenship ? getCitizenshipName(d.citizenship) : '',
          isEmpty: !d.citizenship,
        },
        {
          label: t(`${p}.docNumber.label`),
          value: d.document.number,
          isEmpty: !d.document.number,
        },
      ];
      fields.push({
        label: t(`${p}.expiresAt.label`),
        value: d.document.expires_at
          ? reformatApiDate(d.document.expires_at)
          : '',
        isEmpty: !d.document.expires_at,
      });
      if (d.iin) {
        fields.push({
          label: t(`${p}.iin.label`),
          value: d.iin,
          isEmpty: false,
        });
      }
      return fields;
    }
    return [
      {
        label: t(`${p}.surname.label`),
        value: t(`${p}.surname.value`),
        isEmpty: false,
      },
      {
        label: t(`${p}.name.label`),
        value: t(`${p}.name.value`),
        isEmpty: false,
      },
      {
        label: t(`${p}.gender.label`),
        value: t(`${p}.gender.value`),
        isEmpty: false,
      },
      {
        label: t(`${p}.dob.label`),
        value: t(`${p}.dob.value`),
        isEmpty: false,
      },
      {
        label: t(`${p}.citizenship.label`),
        value: t(`${p}.citizenship.value`),
        isEmpty: false,
      },
      {
        label: t(`${p}.docNumber.label`),
        value: t(`${p}.docNumber.value`),
        isEmpty: false,
      },
      {
        label: t(`${p}.expiresAt.label`),
        value: t(`${p}.expiresAt.value`),
        isEmpty: false,
      },
      {
        label: t(`${p}.iin.label`),
        value: t(`${p}.iin.value`),
        isEmpty: false,
      },
    ];
  });

  const hasEmptyFields = computed(() =>
    reviewFields.value.some(field => field.isEmpty),
  );

  const sheetTitle = computed(() => {
    if (flowStep.value === 'source') {
      return t('passengersData.documentPhoto.titles.addDocument');
    }
    if (flowStep.value === 'photos' || flowStep.value === 'review') {
      return t('passengersData.documentPhoto.titles.newPassenger');
    }
    if (flowStep.value === 'scanning') {
      return t('passengersData.documentPhoto.titles.recognizing');
    }
    if (flowStep.value === 'confirm') {
      return t('passengersData.documentPhoto.confirm.title');
    }
    return null;
  });

  const sheetTitleQaId = computed(() => {
    if (flowStep.value === 'source') {
      return 'passengersData.documentPhoto.titles.addDocument';
    }
    if (flowStep.value === 'photos' || flowStep.value === 'review') {
      return 'passengersData.documentPhoto.titles.newPassenger';
    }
    if (flowStep.value === 'confirm') {
      return 'passengersData.documentPhoto.titles.confirm';
    }
    return 'passengersData.documentPhoto.titles.recognizing';
  });

  const showCloseInHeader = computed(
    () => flowStep.value !== 'scanning' && flowStep.value !== 'error',
  );

  const headerShowBack = computed(
    () =>
      flowStep.value === 'photos' ||
      flowStep.value === 'scanning' ||
      flowStep.value === 'review' ||
      flowStep.value === 'confirm' ||
      flowStep.value === 'error',
  );

  const canAddFiles = computed(() => pickedItems.value.length < maxFiles);

  const galleryMultiple = computed(() => {
    const n = maxFiles - pickedItems.value.length;
    return n > 1;
  });
  const docsMultiple = galleryMultiple;

  const progressSteps = ref<Array<{ key: ProgressKey; state: ProgressState }>>([
    { key: 'uploaded', state: 'pending' },
    { key: 'fields', state: 'pending' },
    { key: 'form', state: 'pending' },
  ]);

  const cancelAbort = () => {
    if (abortController.value) {
      abortController.value.abort();
      abortController.value = null;
    }
  };

  const releaseItems = () => {
    pickedItems.value.forEach(item => {
      if (item.previewUrl) {
        URL.revokeObjectURL(item.previewUrl);
      }
    });
    pickedItems.value = [];
  };

  const resetFlow = () => {
    cancelAbort();
    releaseItems();
    scanResult.value = null;
    errorKind.value = 'recognition';
    flowStep.value = 'source';
  };

  const onSheetOpen = () => {
    resetFlow();
  };

  const onSheetClose = () => {
    cancelAbort();
    releaseItems();
    scanResult.value = null;
    flowStep.value = 'source';
  };

  const onHeaderBack = () => {
    if (flowStep.value === 'confirm') {
      flowStep.value = 'review';
      return;
    }
    if (flowStep.value === 'error') {
      flowStep.value = 'photos';
      return;
    }
    if (flowStep.value === 'review') {
      scanResult.value = null;
      flowStep.value = 'photos';
      return;
    }
    if (flowStep.value === 'scanning') {
      cancelAbort();
      flowStep.value = 'photos';
      return;
    }
    if (flowStep.value === 'photos') {
      releaseItems();
      flowStep.value = 'source';
    }
  };

  const triggerInput = (el: Ref<HTMLInputElement | null>) => {
    const node = el.value;
    if (node) {
      node.value = '';
      node.click();
    }
  };

  const onGalleryClick = () => {
    const node = galleryInputRef.value;
    if (node) {
      const n = maxFiles - pickedItems.value.length;
      node.multiple = n > 1;
    }
    triggerInput(galleryInputRef);
  };

  const onDocsClick = () => {
    const node = docsInputRef.value;
    if (node) {
      const n = maxFiles - pickedItems.value.length;
      node.multiple = n > 1;
    }
    triggerInput(docsInputRef);
  };

  const sourceRows = computed(() => [
    {
      key: 'snapshot' as const,
      icon: 'camera' as const,
      onClick: () => triggerInput(cameraInputRef),
    },
    {
      key: 'choosePhoto' as const,
      icon: 'image' as const,
      onClick: onGalleryClick,
    },
    {
      key: 'uploadFile' as const,
      icon: 'file' as const,
      onClick: onDocsClick,
    },
  ]);

  const photoActions = computed(() => [
    {
      key: 'camera' as const,
      icon: 'camera' as const,
      onClick: () => triggerInput(cameraInputRef),
    },
    {
      key: 'gallery' as const,
      icon: 'image' as const,
      onClick: onGalleryClick,
    },
    {
      key: 'file' as const,
      icon: 'file' as const,
      onClick: onDocsClick,
    },
  ]);

  const appendFiles = (fileList: FileList | null) => {
    if (!fileList?.length) {
      return;
    }
    const remaining = maxFiles - pickedItems.value.length;
    if (remaining <= 0) {
      return;
    }
    const nextFiles = Array.from(fileList).slice(0, remaining);
    const toAdd: PickedItem[] = nextFiles.map(file => {
      const isImage = file.type.startsWith('image/');
      const previewUrl =
        isImage && file.size > 0 ? URL.createObjectURL(file) : null;
      return { id: uuid(), file, previewUrl };
    });
    pickedItems.value = [...pickedItems.value, ...toAdd];
    if (pickedItems.value.length > 0) {
      flowStep.value = 'photos';
    }
  };

  const onFileInput = (event: Event) => {
    const target = event.target as HTMLInputElement;
    appendFiles(target.files);
    target.value = '';
  };

  const removeItem = (id: string) => {
    const next: PickedItem[] = [];
    for (const item of pickedItems.value) {
      if (item.id === id) {
        if (item.previewUrl) {
          URL.revokeObjectURL(item.previewUrl);
        }
      } else {
        next.push(item);
      }
    }
    pickedItems.value = next;
    if (next.length === 0) {
      flowStep.value = 'source';
    }
  };

  const clearPhotos = () => {
    releaseItems();
    flowStep.value = 'source';
  };

  function initProgressSteps() {
    progressSteps.value = [
      { key: 'uploaded', state: 'active' },
      { key: 'fields', state: 'pending' },
      { key: 'form', state: 'pending' },
    ];
  }

  async function runRealScan(files: File[], client: AxiosInstance) {
    const ac = new AbortController();
    abortController.value = ac;
    try {
      const task = await docScanApi.submitRecognize(files, client);
      if (ac.signal.aborted) {
        return;
      }
      progressSteps.value[0].state = 'done';
      progressSteps.value[1].state = 'active';
      const result = await docScanApi.pollResult(task.id, client, ac.signal);
      progressSteps.value[1].state = 'done';
      progressSteps.value[2].state = 'active';
      if (result.error) {
        errorKind.value = 'recognition';
        flowStep.value = 'error';
        return;
      }
      scanResult.value = result.data ?? null;
      progressSteps.value[2].state = 'done';
      flowStep.value = 'review';
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        return;
      }
      errorKind.value = 'technical';
      flowStep.value = 'error';
    } finally {
      abortController.value = null;
    }
  }

  const onRecognize = () => {
    if (pickedItems.value.length === 0 || !props.apiClient) {
      return;
    }
    flowStep.value = 'scanning';
    initProgressSteps();
    runRealScan(
      pickedItems.value.map(item => item.file),
      props.apiClient,
    );
  };

  const onErrorRetry = () => {
    flowStep.value = 'photos';
  };

  onBeforeUnmount(() => {
    cancelAbort();
    releaseItems();
  });
</script>

<style scoped>
  .v3-btn-primary {
    border-radius: 16px;
    min-height: 54px;
    background: #4ebc73;
  }
  .v3-btn-primary:disabled {
    opacity: 0.45;
  }
  .v3-btn-disabled {
    border-radius: 16px;
    min-height: 54px;
    background: rgb(var(--button-secondary-default));
    color: rgb(var(--situational-darkgrey));
    cursor: default;
  }
  .v3-source-tile {
    background: rgb(var(--background-neutral-secondary));
  }
  .v3-hint-card {
    background: rgba(78, 188, 115, 0.12);
  }
</style>
